import React, { useCallback, useMemo, useState } from "react";
import { Button, Table } from "reactstrap";
import formatCompleteDate from "services/formatCompleteDate";
import { BiRefresh } from "react-icons/bi";
import apiGd from "services/apiGd";

const EMPTY_SELECTION = {
  product_categories: [],
  products: [],
  complement_categories: [],
  complements: [],
};

export default function RecoverMenu({
  menu,
  loading,
  getDeletedMenu,
  notify,
  restaurantId,
}) {
  const token = localStorage.getItem("@QRExpress:tokenGd");

  const [selected, setSelected] = useState(EMPTY_SELECTION);
  const [bulkLoading, setBulkLoading] = useState(false);

  const totalSelected = useMemo(
    () => Object.values(selected).reduce((acc, arr) => acc + arr.length, 0),
    [selected]
  );

  const isChecked = useCallback(
    (type, id) => selected[type].includes(id),
    [selected]
  );

  const toggle = useCallback((type, id) => {
    setSelected((prev) => {
      const set = new Set(prev[type]);
      if (set.has(id)) set.delete(id);
      else set.add(id);
      return { ...prev, [type]: [...set] };
    });
  }, []);

  const allIds = useCallback(
    (type) => (menu?.[type] || []).map((item) => item.id),
    [menu]
  );

  const allChecked = useCallback(
    (type) => {
      const ids = allIds(type);
      return ids.length > 0 && ids.every((id) => selected[type].includes(id));
    },
    [allIds, selected]
  );

  const toggleAll = useCallback(
    (type) => {
      setSelected((prev) => {
        const ids = allIds(type);
        const isAll = ids.length > 0 && ids.every((id) => prev[type].includes(id));
        return { ...prev, [type]: isAll ? [] : ids };
      });
    },
    [allIds]
  );

  const clearSelection = useCallback(() => setSelected(EMPTY_SELECTION), []);

  // Recuperação individual (mantém o comportamento antigo do ícone).
  const recover = useCallback(
    async ({ id, type, name }) => {
      if (window.confirm(`Tem certeza que deseja recuperar ${name}?`)) {
        try {
          const reqConfig = {
            headers: { Authorization: `Bearer ${token}` },
          };

          await apiGd.put(
            `/admin/restaurants/menu/recover`,
            {
              id,
              type,
              restaurant_id: restaurantId ? Number(restaurantId) : undefined,
            },
            reqConfig
          );

          getDeletedMenu();
          notify("tr", "success", "Cardápio atualizado com sucesso!");
        } catch (error) {
          console.log(error);
          notify("tr", "danger", "Erro ao recuperar item do cardápio.");
        }
      }
    },
    [token, restaurantId, getDeletedMenu, notify]
  );

  // Recuperação em massa: envia os 4 arrays de uma vez.
  const bulkRecover = useCallback(async () => {
    if (totalSelected === 0) return;
    if (
      !window.confirm(
        `Recuperar ${totalSelected} item(ns) selecionado(s) do cardápio?`
      )
    ) {
      return;
    }

    setBulkLoading(true);
    try {
      const reqConfig = {
        headers: { Authorization: `Bearer ${token}` },
      };

      const response = await apiGd.put(
        `/admin/restaurants/menu/recover`,
        {
          restaurant_id: restaurantId ? Number(restaurantId) : undefined,
          ...selected,
        },
        reqConfig
      );

      const total = response.data?.total ?? totalSelected;
      clearSelection();
      getDeletedMenu();
      notify("tr", "success", `${total} item(ns) recuperado(s) com sucesso!`);
    } catch (error) {
      console.log(error);
      notify("tr", "danger", "Erro ao recuperar itens do cardápio.");
    } finally {
      setBulkLoading(false);
    }
  }, [selected, totalSelected, token, restaurantId, getDeletedMenu, notify, clearSelection]);

  const checkboxCellStyle = { width: 36, textAlign: "center" };

  if (loading) {
    return <>Carregando...</>;
  }

  return (
    <>
      <div style={{ paddingBottom: 80 }}>
        {/* Categorias de Produtos */}
        <h4>Categorias de Produtos</h4>
        <Table>
          <thead>
            <tr>
              <th style={checkboxCellStyle}>
                <input
                  type="checkbox"
                  checked={allChecked("product_categories")}
                  onChange={() => toggleAll("product_categories")}
                  title="Selecionar todos"
                />
              </th>
              <th>Categoria</th>
              <th>Criada em</th>
              <th>Deletada em</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {menu?.product_categories.map((category) => (
              <tr key={category.id}>
                <td style={checkboxCellStyle}>
                  <input
                    type="checkbox"
                    checked={isChecked("product_categories", category.id)}
                    onChange={() => toggle("product_categories", category.id)}
                  />
                </td>
                <td>{category.name}</td>
                <td>
                  {category.created_at
                    ? formatCompleteDate(new Date(category.created_at))
                    : "-"}
                </td>
                <td>
                  {category.deleted_at
                    ? formatCompleteDate(new Date(category.deleted_at))
                    : "-"}
                </td>
                <td>
                  <BiRefresh
                    style={{ color: "red", cursor: "pointer", float: "right" }}
                    size={22}
                    onClick={() =>
                      recover({
                        id: category.id,
                        type: "product_categories",
                        name: category.name,
                      })
                    }
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </Table>

        {/* Produtos */}
        <h4>Produtos</h4>
        <Table>
          <thead>
            <tr>
              <th style={checkboxCellStyle}>
                <input
                  type="checkbox"
                  checked={allChecked("products")}
                  onChange={() => toggleAll("products")}
                  title="Selecionar todos"
                />
              </th>
              <th>Produto</th>
              <th>Preço</th>
              <th>Criada em</th>
              <th>Deletada em</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {menu?.products.map((product) => (
              <tr key={product.id}>
                <td style={checkboxCellStyle}>
                  <input
                    type="checkbox"
                    checked={isChecked("products", product.id)}
                    onChange={() => toggle("products", product.id)}
                  />
                </td>
                <td>{product.name}</td>
                <td>R${product.price}</td>
                <td>
                  {product.created_at
                    ? formatCompleteDate(new Date(product.created_at))
                    : "-"}
                </td>
                <td>
                  {product.deleted_at
                    ? formatCompleteDate(new Date(product.deleted_at))
                    : "-"}
                </td>
                <td>
                  <BiRefresh
                    style={{ color: "red", cursor: "pointer", float: "right" }}
                    size={22}
                    onClick={() =>
                      recover({
                        id: product.id,
                        type: "products",
                        name: product.name,
                      })
                    }
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </Table>

        {/* Categorias de Complementos */}
        <h4>Categorias de Complementos</h4>
        <Table>
          <thead>
            <tr>
              <th style={checkboxCellStyle}>
                <input
                  type="checkbox"
                  checked={allChecked("complement_categories")}
                  onChange={() => toggleAll("complement_categories")}
                  title="Selecionar todos"
                />
              </th>
              <th>Categoria</th>
              <th>Criada em</th>
              <th>Deletada em</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {menu?.complement_categories.map((category) => (
              <tr key={category.id}>
                <td style={checkboxCellStyle}>
                  <input
                    type="checkbox"
                    checked={isChecked("complement_categories", category.id)}
                    onChange={() =>
                      toggle("complement_categories", category.id)
                    }
                  />
                </td>
                <td>{category.name}</td>
                <td>
                  {category.created_at
                    ? formatCompleteDate(new Date(category.created_at))
                    : "-"}
                </td>
                <td>
                  {category.deleted_at
                    ? formatCompleteDate(new Date(category.deleted_at))
                    : "-"}
                </td>
                <td>
                  <BiRefresh
                    style={{ color: "red", cursor: "pointer", float: "right" }}
                    size={22}
                    onClick={() =>
                      recover({
                        id: category.id,
                        type: "complement_categories",
                        name: category.name,
                      })
                    }
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </Table>

        {/* Complementos */}
        <h4>Complementos</h4>
        <Table>
          <thead>
            <tr>
              <th style={checkboxCellStyle}>
                <input
                  type="checkbox"
                  checked={allChecked("complements")}
                  onChange={() => toggleAll("complements")}
                  title="Selecionar todos"
                />
              </th>
              <th>Complemento</th>
              <th>Preço</th>
              <th>Criada em</th>
              <th>Deletada em</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {menu?.complements.map((complement) => (
              <tr key={complement.id}>
                <td style={checkboxCellStyle}>
                  <input
                    type="checkbox"
                    checked={isChecked("complements", complement.id)}
                    onChange={() => toggle("complements", complement.id)}
                  />
                </td>
                <td>{complement.name}</td>
                <td>R${complement.price}</td>
                <td>
                  {complement.created_at
                    ? formatCompleteDate(new Date(complement.created_at))
                    : "-"}
                </td>
                <td>
                  {complement.deleted_at
                    ? formatCompleteDate(new Date(complement.deleted_at))
                    : "-"}
                </td>
                <td>
                  <BiRefresh
                    style={{ color: "red", cursor: "pointer", float: "right" }}
                    size={22}
                    onClick={() =>
                      recover({
                        id: complement.id,
                        type: "complements",
                        name: complement.name,
                      })
                    }
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      {/* Botão flutuante de recuperação em massa */}
      {totalSelected > 0 && (
        <div
          style={{
            position: "fixed",
            right: 32,
            bottom: 32,
            zIndex: 1050,
            display: "flex",
            alignItems: "center",
            gap: 12,
            background: "#fff",
            border: "1px solid #e0e0e0",
            borderRadius: 40,
            boxShadow: "0 4px 16px rgba(0,0,0,0.18)",
            padding: "8px 12px 8px 18px",
          }}
        >
          <span
            style={{
              fontFamily: "Poppins",
              fontSize: 14,
              fontWeight: 600,
              color: "#333",
            }}
          >
            {totalSelected} selecionado(s)
          </span>
          <Button
            color="link"
            style={{ color: "#6c757d", padding: "6px 8px" }}
            onClick={clearSelection}
            disabled={bulkLoading}
          >
            Limpar
          </Button>
          <Button
            color="success"
            style={{ borderRadius: 40, padding: "8px 20px" }}
            onClick={bulkRecover}
            disabled={bulkLoading}
          >
            <BiRefresh size={20} style={{ marginRight: 6, verticalAlign: "middle" }} />
            {bulkLoading ? "Recuperando..." : "Recuperar selecionados"}
          </Button>
        </div>
      )}
    </>
  );
}
